<div cdk-overlay-origin #trigger class="select-container" [class.if-inline]="inline()" [class.select-container--open]="isOpen && !inline()">
  @if (inline()) {
    <!--
      Inline picker: the editor's text button with a chevron, as "Add part
      group" (part-group-picker), opening an `.action-menu` list.
    -->
    <app-button
      #inlineTrigger
      class="select-inline-trigger"
      variant="text"
      size="sm"
      [disabled]="disabled"
      [ariaLabel]="inlineAriaLabel"
      ariaHaspopup="listbox"
      [ariaExpanded]="isOpen"
      (click)="toggleDropdown()"
      (keydown)="onTriggerKeydown($event)">
      <span class="select-inline-label">
        @if (type() === 'custom-dropdown') {
          <ng-content></ng-content>
        } @else {
          {{ selectedLabel }}
        }
      </span>
      @if (selectedBadge) {
        <span class="select-inline-badge">{{ selectedBadge }}</span>
      }
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="-4 -4 32 32"
        stroke-width="2"
        stroke="currentColor"
        aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
      </svg>
    </app-button>
  } @else {
    <button
      #boxedTrigger
      type="button"
      class="select-trigger"
      (click)="toggleDropdown($event)"
      (keydown)="onBoxedTriggerKeydown($event)"
      [attr.aria-label]="ariaLabel() ? ariaLabel() + ': ' + selectedLabel : null"
      [attr.aria-describedby]="ariaDescribedBy()"
      [attr.aria-expanded]="isOpen"
      [attr.aria-controls]="isOpen ? listboxId : null"
      aria-haspopup="listbox"
      [disabled]="disabled"
      [class.placeholder-visible]="placeholderVisible">
      <span class="select-trigger-label">{{ selectedLabel }}</span>
      @if (selectedBadge) {
        <span class="select-trigger-badge">{{ selectedBadge }}</span>
      }
    </button>
  }
  @if ((multi() && selected && selected.length > 0 && !disabled) || (clearable() && selected && !multi() && !disabled)) {
    <button type="button" class="select-clear" (click)="clearSelection($event)" aria-label="Clear selection">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
      </svg>
    </button>
  }
  <!-- Search input for typeahead functionality -->
  <ng-template #searchBlock>
    @if (searchable()) {
      <div class="select-search">
        <app-input
          #searchInput
          [placeholder]="searchPlaceholder()"
          [(ngModel)]="searchValue"
          (ngModelChange)="onSearchValueChange()"
          class="select-search-input">
        </app-input>
        @if (searchTerm.length > 0) {
          <app-button variant="ghost" (click)="clearSearch()" class="select-search-clear" aria-label="Clear search">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </app-button>
        }
      </div>
    }
  </ng-template>
  <ng-template
    cdk-connected-overlay
    cdkConnectedOverlayLockPosition
    [cdkConnectedOverlayHasBackdrop]="!inline()"
    cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
    [cdkConnectedOverlayFlexibleDimensions]="true"
    [cdkConnectedOverlayDisableClose]="true"
    [cdkConnectedOverlayOrigin]="trigger"
    [cdkConnectedOverlayOpen]="isOpen"
    [cdkConnectedOverlayPositions]="inline() ? inlineOverlayPositions : overlayPositions"
    [cdkConnectedOverlayMinWidth]="inline() ? 0 : originWidth"
    (attach)="onOverlayAttached()"
    (detach)="isOpen = false"
    (backdropClick)="isOpen = false"
    (overlayOutsideClick)="onOutsideClick($event)">
    @if (inline()) {
      <div
        #optionsContainer
        class="action-menu select-menu"
        role="listbox"
        [attr.aria-label]="ariaLabel() || placeholder()"
        [attr.aria-multiselectable]="multi() || null"
        (keydown)="onMenuKeydown($event)">
        <ng-container *ngTemplateOutlet="searchBlock" />
        @if (loading()) {
          <p class="action-menu__header">Loading…</p>
        } @else if (menuOptions.length === 0) {
          <p class="action-menu__header">{{ isSearching ? 'No results found' : 'Nothing to choose' }}</p>
        } @else {
          @for (option of menuOptions; track $index) {
            <button
              #optionItem
              type="button"
              role="option"
              tabindex="-1"
              class="action-menu__item"
              [attr.aria-selected]="isSelected(option)"
              [attr.aria-disabled]="isOptionDisabled(option) ? 'true' : null"
              (click)="selectOption(option)">
              <span class="action-menu__label">{{ optionLabel(option) }}</span>
              @if (optionMeta(option); as meta) {
                <span class="action-menu__meta">{{ meta }}</span>
              }
              @if (isSelected(option)) {
                <svg
                  class="select-menu__check"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                  stroke="currentColor"
                  aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
              }
            </button>
          }
        }
      </div>
    } @else {
      <div
        #listbox
        class="select-dropdown"
        role="listbox"
        [id]="listboxId"
        [attr.tabindex]="searchable() ? null : -1"
        [attr.aria-label]="ariaLabel()"
        [attr.aria-multiselectable]="multi() || null"
        [attr.aria-activedescendant]="searchable() ? null : activeDescendant"
        (keydown)="onListboxKeydown($event)"
        @fadeInOnEnter
        @slideInUpOnEnter>
        <ng-container *ngTemplateOutlet="searchBlock" />

        <!-- Loading state -->
        @if (loading()) {
          <div class="select-loading">Loading…</div>
        }

        <!-- No results message -->
        @if (!loading() && searchable() && isSearching && currentOptions.length === 0) {
          <div class="select-no-results">No results found</div>
        }

        <!-- Options list -->
        @if (!loading() && currentOptions.length > 0) {
          <div class="select-options" #optionsContainer>
            <!-- Available options -->
            @for (option of availableOptions; track $index; let i = $index) {
              <div
                class="select-option"
                [id]="optionId(i)"
                [class.multi]="multi()"
                [class.disabled]="isOptionDisabled(option)"
                role="option"
                [class.highlighted]="i === highlightedIndex"
                [class.keyboard-active]="i === highlightedIndex && keyboardActive"
                [attr.aria-selected]="isSelected(option)"
                [attr.aria-disabled]="isOptionDisabled(option)"
                (click)="selectOption(option)"
                (mousemove)="onOptionPointer(i)"
                #optionItem>
                @if (multi()) {
                  <input type="checkbox" class="select-checkbox" [checked]="isSelected(option)" [disabled]="isOptionDisabled(option)" />
                }
                <span class="select-option-label">{{ bindLabel() ? option[bindLabel()!] : option }}</span>
                @if (bindBadge() && option[bindBadge()!]) {
                  <span class="select-option-badge">{{ option[bindBadge()!] }}</span>
                }
              </div>
            }

            <!-- Locked options, grouped by lock reason (subscription by default) -->
            @for (group of lockedOptionGroups; track group.label) {
              <div class="select-group-header">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="lock-icon">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                </svg>
                <span>{{ group.label }}</span>
              </div>
              @for (option of group.options; track $index) {
                <div
                  class="select-option locked"
                  [class.multi]="multi()"
                  role="option"
                  [attr.aria-selected]="isSelected(option)"
                  [attr.aria-disabled]="true"
                  (click)="selectOption(option)"
                  #optionItem>
                  @if (multi()) {
                    <input type="checkbox" class="select-checkbox" [checked]="isSelected(option)" [disabled]="true" />
                  }
                  <span class="select-option-label">{{ bindLabel() ? option[bindLabel()!] : option }}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="lock-icon">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                  </svg>
                </div>
              }
            }
          </div>
        }
      </div>
    }
  </ng-template>
</div>
